import { inject, Injectable } from '@angular/core';
import { AanAfwezigheid } from 'src/app/afas-classes/aanafwezigheid.class';
import { Hourtype } from 'src/app/afas-classes/hourtype';
import { Realisation, RealisationHourtype } from 'src/app/classes/realisation';
import { Settlement, SettlementHourtype } from 'src/app/classes/settlement.class';
import { User } from 'src/app/classes/user.class';
import { EmployeeService } from 'src/app/services/employee.service';
import {
    CONTRACT_HOURTYPE_CODES,
    overtimePercentageOfBonusCode,
    SettlementsService,
} from 'src/app/services/settlements.service';
import {
    HOUR_MINUTES,
    MONDAY_INDEX,
    SATURDAY_INDEX,
    SUNDAY_INDEX,
    Utils,
    WORK_WEEK_MINUTES,
} from 'src/app/utils.class';
import { Week } from '../../classes/week';
import { WeekDay } from '../../classes/weekday';
import { Settleable } from './settleable.interface';

export enum BONUS {
    /** Gewerkte uren; Gewerkt */
    GU = 'GU',
    /** Verschoven uren 30% (nacht); Gewerkt - extra tarief */
    '130V' = '130V',
    /** Verschoven uren 50%; Gewerkt - extra tarief */
    '150V' = '150V',
    /** Verschoven uren 100%; Gewerkt - extra tarief */
    '200V' = '200V',
}

/**
 * De eerste twee uren voor en na de daguren zijn 130%, met een maximum van drie uren per dag.
 * Alles boven de 11 uur (8 x 100% + 3 x 130%) is 150%.
 */
const OVERTIME_130_MAX_MINUTES = 180;

type Timeframe = {
    /** Realisatie waar dit tijdsvak van is afgeleid */
    realisation: Realisation;
    /** Aantal gewerkte minutes in dit tijdsvak */
    minutes: number;
    /** Starttijd van dit tijdsvak */
    from: Date;
    /** Eindtijd van dit tijdsvak */
    to: Date;
    /** Toeslag tarief van dit tijdsvak */
    type: BONUS;
};

type TimeframeBonus = {
    /** Starttijd van dit tijdsvak */
    from: Date;
    /** Eindtijd van dit tijdsvak */
    to: Date;
    /** Toeslag tarief van dit tijdsvak */
    type: BONUS;
};

@Injectable()
export class TracklineSettlementService implements Settleable {
    private __employee: Readonly<User>;

    set employee(value: Readonly<User>) {
        this.__employee = value;

        this.hasCaoBouwInfra = this.employeeService.hasCaoBouwInfra(this.__employee);
    }

    get employee(): Readonly<User> {
        return this.__employee;
    }

    private hasCaoBouwInfra: boolean;

    hourTypes: Hourtype[];
    realisations: Realisation[];

    updateForm: (settlements: Settlement[], day: WeekDay) => void;

    aanAfwezigheid: AanAfwezigheid[] = [];
    fcHourTypeMap = new Map<string, string>([
        ['200', 'overtime200'],
        ['200Z', 'overtime200s'],
        ['200V', 'bonus100'],
        ['150', 'overtime150'],
        ['150Z', 'overtime150s'],
        ['150V', 'overtime150'],
        ['145', 'overtime145'],
        ['140', 'overtime140'],
        ['135', 'overtime135'],
        ['130', 'overtime130'],
        ['125', 'overtime125'],
        ['100', 'overtime100'],
        ['S100', 'overtime100'],
        ['S100W', 'overtime100'],
        ['100B', 'overtime100'],
        ['SDW', 'overtime100'],
        ['150V', 'bonus50'],
        ['145V', 'bonus45'],
        ['135V', 'bonus35'],
        ['130V', 'bonus30'],
        [SettlementHourtype.travelHours, 'traveltime'],
        [SettlementHourtype.travelHoursActual, 'traveltime'],
        ['CRW', 'travelcost'],
        ['UITV_MIN2', 'notPaidHours'],
    ]);

    readonly OVERTIME_HOURTYPE_CODES = ['200', '200Z', '150', '150Z', '145', '140', '135', '130', '125', '100', '100B'];

    /**
     * Uurcodes die samen het contracturenbudget van de dag verbruiken (max. 480 minuten netto).
     * Meerwerk (MU) valt hier per definitie buiten, dat zit juist boven de contracturen.
     */
    static readonly CONTRACT_BUDGET_HOURTYPE_CODES = new Set(CONTRACT_HOURTYPE_CODES.filter(c => c !== 'MU'));

    private sleepOrLeaveHourCodes = new Set<string>();

    private readonly settlementService = inject(SettlementsService);
    private readonly employeeService = inject(EmployeeService);

    constructor() {
        CONTRACT_HOURTYPE_CODES.filter(c => c !== 'MU').forEach(cht => {
            this.fcHourTypeMap.set(cht, 'normal');
        });
    }

    async settleWeekHours(week: Week) {
        week.settling = true;

        //* Each of these this.settle<Type> methods include a saveSettlements call
        try {
            week.weekDays.forEach(d => (d.settlements = []));

            if (week.sendToAfas) {
                return;
            }

            await Promise.all(
                week.weekDays.map(day =>
                    this.settlementService.settleLeaveDays(
                        day,
                        this.aanAfwezigheid,
                        this.hourTypes,
                        this.fcHourTypeMap,
                        this.employee.id,
                        this.sleepOrLeaveHourCodes,
                        this.updateForm
                    )
                )
            );

            // Op volgorde verrekenen: het GU-budget van de week wordt chronologisch gevuld, dus
            // een dag moet weten hoeveel contracturen er eerder in de week al geboekt zijn.
            for (const day of week.weekDays) {
                await this.settleDayHours(day, week);
            }

            if (this.hasCaoBouwInfra) {
                await this.settlementService.settleBouwCaoWeekHours(
                    week,
                    this.employee,
                    this.fcHourTypeMap,
                    this.realisations,
                    this.hourTypes,
                    this.sleepOrLeaveHourCodes,
                    this.updateForm
                );
            } else {
                // Cao Beroepsgoederenvervoer: dagen met te weinig uren worden niet gratis
                // aangevuld, maar gevuld met overuren van andere dagen in dezelfde week.
                await this.settlementService.settleChauffeurShortages(
                    week,
                    this.employee,
                    this.fcHourTypeMap,
                    this.hourTypes,
                    this.OVERTIME_HOURTYPE_CODES,
                    this.updateForm
                );
            }
        } finally {
            week.settling = false;
        }
    }

    private async settleDayHours(day: WeekDay, week: Week) {
        day.settling = true;

        try {
            if (day.sendToAfas) {
                return;
            }

            // Slaapuren
            day.settlements.push(
                ...this.settlementService.settleRealisationsForHourTypes(
                    day.realisations,
                    [RealisationHourtype.sleep],
                    day.date.getDay() > 0 && day.date.getDay() < 6 ? 'SDU' : 'SDW',
                    this.hourTypes
                )
            );

            // Scholing
            day.settlements.push(
                ...this.settlementService.settleEducation(
                    day.settlements,
                    day.realisations,
                    day.date.getDay() > 0 && day.date.getDay() < 6 ? 'S100' : 'S100W',
                    day.fg.value,
                    this.hourTypes
                )
            );

            // Werk uren
            this.settleWorkingHours(day, week);

            // Pauze verrekenen
            this.settlementService.settlePauzes(day, day.fg.value.pause, this.hourTypes);

            if (this.hasCaoBouwInfra) {
                // Cao uren
                this.settlementService.settleCaoHours(
                    day,
                    week,
                    this.realisations,
                    this.employee,
                    this.hourTypes,
                    this.sleepOrLeaveHourCodes
                );
            }

            // Verblijfskostenvergoeding
            day.settlements.push(
                ...this.settlementService.settleVerblijfskosten(
                    day,
                    this.employee,
                    this.hasCaoBouwInfra,
                    this.OVERTIME_HOURTYPE_CODES,
                    this.hourTypes
                )
            );

            // Reistijd
            const travelSettlements = await this.settlementService.settleTravelTime(day, this.employee, this.hourTypes);
            day.settlements.push(...travelSettlements);

            // Reiskosten
            const travelCostSettlements = await this.settlementService.settleTravelCosts(
                day,
                this.hasCaoBouwInfra,
                this.employee,
                this.hourTypes
            );
            day.settlements.push(...travelCostSettlements);

            await this.settlementService.saveSettlements(day, this.employee.id, this.fcHourTypeMap, this.updateForm);
        } finally {
            day.settling = false;
        }
    }

    private settleWorkingHours(day: WeekDay, week?: Week) {
        const timeframes: Timeframe[] = [];

        day.realisations
            .filter(r =>
                [
                    RealisationHourtype.loading_unloading,
                    RealisationHourtype.driving_to,
                    RealisationHourtype.worktime,
                    RealisationHourtype.driving_back,
                ].includes(r.hourtype)
            )
            .forEach(realisation => {
                this.calculateRealisationTimeframes(realisation, timeframes);
            });

        // TODO: remove after dev done
        if (timeframes.length === 0) {
            return;
        }

        this.handleDaySettlements(
            day,
            timeframes.sort((a, b) => Utils.getTimeOrNull(a.from) - Utils.getTimeOrNull(b.from)),
            week
        );
    }

    /**
     * Contracturen die eerder deze week al geboekt zijn. Bepaalt hoeveel van het weekbudget nog
     * over is voordat uren als meerwerk (MU) geboekt worden.
     *
     * Niet alleen GU: ook verlof (V, BV), ziekte, feestdagen, scholing en slaapuren zijn
     * contracturen en verbruiken dus het weekbudget.
     *
     * Alleen de dagen vóór {@link day} tellen mee: de week wordt chronologisch gevuld.
     */
    private contractMinutesEarlierThisWeek(day: WeekDay, week?: Week) {
        if (!week) {
            return 0;
        }

        return week.weekDays
            .filter(d => d.date.getTime() < day.date.getTime())
            .flatMap(d => d.settlements)
            .filter(s => !s.bonus && TracklineSettlementService.CONTRACT_BUDGET_HOURTYPE_CODES.has(s.hourtype_code))
            .reduce((sum, settlement) => sum + settlement.minutes, 0);
    }

    private handleDaySettlements(day: WeekDay, timeframes: Timeframe[], week?: Week) {
        const employeeWorkDayMinutes = day.fg.value.contractHours;

        const pauseMinutes = day.fg.value.pause;

        /**
         * Contracturen die al geboekt zijn voordat de gewerkte uren worden verrekend: slaapuren,
         * scholing en verlof (F/V/Vorst e.d.). Deze vallen ook onder de contracturen en verbruiken
         * dus een deel van het dagbudget, waardoor er minder GU overblijft.
         */
        const preBookedContractMinutes = day.settlements
            .filter(s => !s.bonus && TracklineSettlementService.CONTRACT_BUDGET_HOURTYPE_CODES.has(s.hourtype_code))
            .map(s => s.minutes)
            .reduce((sum, current) => sum + current, 0);

        /** Contractminuten die tijdens deze verrekening uit de gewerkte tijdsvakken zijn geboekt. */
        let settledContractMinutes = 0;

        /**
         * Maximaal aantal contracturen (GU + MU) dat uit de gewerkte tijdsvakken geboekt mag
         * worden op deze dag; alles daarboven is overwerk.
         *
         * De pauze komt er bruto bij op: settlePauzes haalt die er ná het verrekenen weer af, dus
         * een standaard werkdag van 480 netto contracturen is 510 minuten aanwezig. Al geboekte
         * contracturen (verlof, slaapuren, scholing) gaan er netto af, want daar zit geen pauze in.
         */
        const workdayMinutes = employeeWorkDayMinutes + pauseMinutes - preBookedContractMinutes;

        /**
         * Contracturen van de medewerker per week in minuten, bijv. 32 uur = 1920 minuten. Tot dat
         * aantal krijgen de uren code GU; daarboven tot 40 uur per week code MU (meerwerk).
         */
        const employeeWeekMinutes = (this.employee?.contract_hours ?? 0) * HOUR_MINUTES;

        /** Contracturen (GU en MU) die eerder deze week al geboekt zijn. */
        const contractMinutesEarlier = this.contractMinutesEarlierThisWeek(day, week);

        /** Netto GU-budget dat deze week nog over is; de week wordt chronologisch gevuld. */
        const netGuMinutesLeftThisWeek = Math.max(0, employeeWeekMinutes - contractMinutesEarlier);

        /**
         * Hoeveel GU er vandaag nog geboekt mag worden, in bruto minuten.
         *
         * Binnen de loop worden bruto minuten geteld, terwijl settlePauzes de pauze pas ná het
         * verrekenen van de contracturen afhaalt (en die van GU pakt, het laagste tarief). Daarom
         * komt de pauze er bruto bij op - maar alleen als de dag ook echt op GU eindigt. Vult GU
         * het dagbudget niet, dan gaat de pauze van de MU-uren af en is de correctie onnodig.
         *
         * Zonder week-context (losse dagverrekening) valt dit terug op het dagbudget.
         */
        const guMinutesLeftThisWeek = week
            ? netGuMinutesLeftThisWeek + (netGuMinutesLeftThisWeek >= workdayMinutes - pauseMinutes ? pauseMinutes : 0)
            : workdayMinutes;

        /**
         * Begin- en eindtijd van de dienst. Dit zijn eigenschappen van de hele dag en moeten dus
         * buiten de loop bepaald worden: `day.settlements` groeit tijdens de loop en bevat ook
         * slaapuren/scholing, waardoor het geen betrouwbare bron is voor het begin van het werk.
         */
        const startDate = timeframes
            .map(t => t.from)
            .reduce((earliest, current) => (current.getTime() < earliest.getTime() ? current : earliest));
        const endDate = timeframes
            .map(t => t.to)
            .reduce((latest, current) => (current.getTime() > latest.getTime() ? current : latest));

        /** Bij start werkdag om 00.00 uur en doorwerken na 05.00 uur tellen uren na 05.00 voor 150% */
        const isEarlyMorning = this.isStartedOn0000AndContinueAfter0500(startDate, endDate, day);

        /**
         * Aantal minuten dat vandaag al op 130% geboekt is. De band is maximaal
         * {@link OVERTIME_130_MAX_MINUTES}; daarna is alle overwerk 150%.
         */
        let settled130Minutes = 0;

        for (const timeframe of timeframes) {
            const { type: bonusHourType, realisation, to, from } = timeframe;

            /** Day of the week for the timeframe's realisation's bookdate (0 = Sunday, 6 = Saturday) */
            const timeframeDay = new Date(timeframe.realisation.bookdate).getDay();

            const hourType = BONUS.GU;

            /**
             * Contractminuten die binnen dit tijdsvak zijn geboekt. Een tijdsvak kan in meerdere
             * delen verrekend worden (bijv. op maandag rond 04:00), dus het budget moet ook binnen
             * het tijdsvak meelopen.
             */
            let settledInTimeframe = 0;

            /** Helper function for less typing later on */
            const settle = ({
                settleFrom = from,
                settleHourType = hourType,
                settleTo = to,
            }: {
                settleFrom?: Date;
                settleHourType?: string;
                settleTo?: Date;
            } = {}) => {
                day.settlements.push(
                    this.settlementService.createSettlementFromRealisation(
                        realisation,
                        this.hourTypes,
                        Utils.minuteDuration(settleTo, settleFrom),
                        settleHourType,
                        settleFrom,
                        settleTo
                    )
                );
            };

            /** Helper function for less typing later on */
            const settleBonus = ({
                settleBonusHourType = bonusHourType,
                settleFrom = from,
                settleHourType = hourType,
                settleTo = to,
            }: {
                settleBonusHourType?: string;
                settleFrom?: Date;
                settleHourType?: string;
                settleTo?: Date;
            } = {}) => {
                const minutes = Utils.minuteDuration(settleTo, settleFrom);

                const bonus = this.settlementService.createSettlementFromRealisation(
                    realisation,
                    this.hourTypes,
                    minutes,
                    settleBonusHourType,
                    settleFrom,
                    settleTo
                );

                const normal = this.settlementService.createSettlementFromRealisation(
                    realisation,
                    this.hourTypes,
                    minutes,
                    settleHourType,
                    settleFrom,
                    settleTo
                );

                normal.bonus = bonus;
                bonus.normal = normal;

                day.settlements.push(normal, bonus);
            };

            /** {@link timeframeDay} is Saturday */
            const isSaturday = timeframeDay === SATURDAY_INDEX;

            /** {@link timeframeDay} is Sunday */
            const isSunday = timeframeDay === SUNDAY_INDEX;

            /** {@link timeframeDay} is in the weekend */
            const isWeekend = isSaturday || isSunday;

            /** {@link timeframeDay} is a weekday */
            const isWeekday = !isWeekend;

            /** {@link timeframeDay} is Monday */
            const isMonday = timeframeDay === MONDAY_INDEX;

            const isHoliday = !!day.settlements.find(s => s.hourtype_code === 'F'); // Feestdag uren; Verlof

            /**
             * Boekt een deel van een tijdsvak als contracturen (GU of MU) met de bijbehorende
             * V-opslag. Voor MU gelden dezelfde opslagregels als voor GU.
             */
            const settleContractPart = (hourTypeCode: string, partFrom: Date, partTo: Date, bonusType: BONUS) => {
                if (bonusType === BONUS.GU) {
                    settle({ settleHourType: hourTypeCode, settleFrom: partFrom, settleTo: partTo });
                } else {
                    settleBonus({
                        settleBonusHourType: bonusType,
                        settleHourType: hourTypeCode,
                        settleFrom: partFrom,
                        settleTo: partTo,
                    });
                }

                settledInTimeframe += Utils.minuteDuration(partTo, partFrom);
            };

            /**
             * Verrekent weekenduren. Zolang er tussen de contracturen en 40 uur per week nog
             * ruimte is, worden die uren meerwerk (MU) met de weekendtoeslag erbij; daarboven
             * gelden de weekendtarieven 150Z/200Z.
             *
             * Weekenduren leveren nooit GU op: het zijn geen contracturen.
             */
            const settleWeekendHours = ({
                weekendBonusHourType,
                weekendHourType,
                weekendFrom = from,
                weekendTo = to,
            }: {
                weekendBonusHourType: BONUS;
                weekendHourType: string;
                weekendFrom?: Date;
                weekendTo?: Date;
            }) => {
                const spanMinutes = Utils.minuteDuration(weekendTo, weekendFrom);

                /** Ruimte tot 40 uur per week; MU vult die ruimte op. */
                const muMinutes = Math.max(
                    0,
                    Math.min(
                        spanMinutes,
                        WORK_WEEK_MINUTES - contractMinutesEarlier - settledContractMinutes - settledInTimeframe
                    )
                );

                if (muMinutes > 0) {
                    const muEnd = Utils.addMinutes(new Date(weekendFrom), muMinutes);

                    settleContractPart(SettlementHourtype.moreWorkingHours, weekendFrom, muEnd, weekendBonusHourType);
                }

                if (spanMinutes > muMinutes) {
                    // Boven de 40 uur: geen contracturen meer, alleen het weekendtarief.
                    settle({
                        settleHourType: weekendHourType,
                        settleFrom: Utils.addMinutes(new Date(weekendFrom), muMinutes),
                        settleTo: weekendTo,
                    });
                }
            };

            /**
             * Verrekent contracturen tot het dagbudget vol is; het restant gaat als overwerk weg.
             * Dit is de enige plek waar GU en MU ontstaan, zodat er nooit meer dan het dagbudget
             * (standaard 480 minuten) aan contracturen op een dag geboekt kan worden.
             *
             * Binnen dat dagbudget wordt eerst GU gevuld tot de contracturen van de week op zijn
             * (bijv. 32 uur), daarna MU tot maximaal 40 uur per week.
             */
            const settleContractHours = ({
                budgetBonusHourType = bonusHourType,
                budgetFrom = from,
                budgetTo = to,
            }: {
                budgetBonusHourType?: BONUS;
                budgetFrom?: Date;
                budgetTo?: Date;
            } = {}) => {
                const settledSoFar = settledContractMinutes + settledInTimeframe;

                /** Ruimte binnen het dagbudget; GU en MU samen mogen hier niet boven komen. */
                const budgetLeft = workdayMinutes - settledSoFar;

                if (budgetLeft <= 0) {
                    // Budget is al op, dus alles is overwerk
                    settleOvertime({ overtimeFrom: budgetFrom, overtimeTo: budgetTo });
                    return;
                }

                const spanMinutes = Utils.minuteDuration(budgetTo, budgetFrom);

                /** Zoveel van dit tijdsvak past nog binnen de contracturen van deze dag. */
                const contractPartMinutes = Math.min(spanMinutes, budgetLeft);

                /** Daarvan mag dit deel nog als GU; de rest wordt meerwerk (MU). */
                const guPartMinutes = Math.max(0, Math.min(contractPartMinutes, guMinutesLeftThisWeek - settledSoFar));

                let partFrom = budgetFrom;

                if (guPartMinutes > 0) {
                    const guEnd = Utils.addMinutes(new Date(partFrom), guPartMinutes);

                    settleContractPart(BONUS.GU, partFrom, guEnd, budgetBonusHourType);

                    partFrom = guEnd;
                }

                if (contractPartMinutes > guPartMinutes) {
                    // Contracturen van de week zijn op: de rest is meerwerk tot 40 uur per week.
                    const muEnd = Utils.addMinutes(new Date(budgetFrom), contractPartMinutes);

                    settleContractPart(SettlementHourtype.moreWorkingHours, partFrom, muEnd, budgetBonusHourType);

                    partFrom = muEnd;
                }

                if (spanMinutes > contractPartMinutes) {
                    // Boven het dagbudget: overwerk.
                    settleOvertime({ overtimeFrom: partFrom, overtimeTo: budgetTo });
                }
            };

            const settleWeekdays = () => settleContractHours();

            const settleSaturday = () => {
                // Op zaterdag alles overwerk; van 00:00 tot 21:00 150%, daarna 200%

                const saturday2100 = Utils.setTime(new Date(day.date), 21, 0);

                if (from.getTime() >= saturday2100.getTime()) {
                    // Na 21:00 uur op zaterdag alles 200%
                    settleWeekendHours({ weekendBonusHourType: BONUS['200V'], weekendHourType: '200Z' });
                } else if (to.getTime() > saturday2100.getTime()) {
                    // Indien tijdvakken over 21:00 uur op zaterdag, splitsen - tot 21:00 en daarna
                    settleWeekendHours({
                        weekendBonusHourType: BONUS['150V'],
                        weekendHourType: '150Z',
                        weekendTo: saturday2100,
                    });

                    settleWeekendHours({
                        weekendBonusHourType: BONUS['200V'],
                        weekendHourType: '200Z',
                        weekendFrom: saturday2100,
                    });
                } else {
                    settleWeekendHours({ weekendBonusHourType: BONUS['150V'], weekendHourType: '150Z' });
                }
            };

            const settleMonday = () => {
                const monday0400 = Utils.setTime(new Date(day.date), 4, 0);

                // Uren op maandag van 00.00 uur tot 04.00 uur zijn 200% toeslag
                if (to.getTime() <= monday0400.getTime()) {
                    settleContractHours();
                } else if (from.getTime() < monday0400.getTime()) {
                    // Indien tijdvakken over 04:00 uur op maandag, splitsen in 2 settlements - tot 04:00 en daarna
                    settleContractHours({ budgetTo: monday0400 });

                    settleContractHours({ budgetFrom: monday0400 });
                } else if (isEarlyMorning) {
                    settleContractHours({ budgetBonusHourType: BONUS['150V'] });
                } else {
                    settleWeekdays();
                }
            };

            /**
             * Bij start werkdag om 00.00 uur en doorwerken na 05.00 uur zijn uren na 05.00 150% toeslag
             * Uren voor 05:00 zijn ook 150% toeslag, per tarief
             */
            const settleEarlyMornings = () => {
                const day0500 = Utils.setTime(new Date(day.date), 5, 0);

                if (
                    // volledig voor 05:00 uur
                    from.getTime() < day0500.getTime() &&
                    to.getTime() <= day0500.getTime()
                ) {
                    settleContractHours();
                } else if (
                    // volledig na 05:00 uur
                    from.getTime() >= day0500.getTime()
                ) {
                    settleContractHours({ budgetBonusHourType: BONUS['150V'] });
                } else {
                    // Over 05:00 uur heen: splitsen, zodat er nooit uren verdwijnen als de
                    // blokindeling verandert.
                    settleContractHours({ budgetTo: day0500 });

                    settleContractHours({ budgetBonusHourType: BONUS['150V'], budgetFrom: day0500 });
                }
            };

            /**
             * Verrekent overwerk. Wordt alleen aangeroepen voor minuten die buiten het
             * contracturenbudget vallen; de GU/overwerk-splitsing zit in {@link settleContractHours}.
             *
             * Per minuut geldt het hoogste van twee percentages:
             * - het tijdvak waarin de minuut valt (bijv. 18:00-24:00 is 150%);
             * - de overwerktrap: de eerste 3 uur overwerk is 130%, daarboven 150%.
             *
             * De 3 uur is een budget aan minuten die ook daadwerkelijk op 130% uitbetaald worden.
             * Valt een minuut in een 150%- of 200%-tijdvak, dan wint dat tijdvak en verbruikt die
             * minuut de 130%-band dus niet.
             */
            const settleOvertime = ({
                overtimeFrom = from,
                overtimeTo = to,
            }: { overtimeFrom?: Date; overtimeTo?: Date } = {}) => {
                /**
                 * Toeslagpercentage van het tijdvak waarin dit tijdsvak valt. De BONUS-waarden zijn
                 * dezelfde toeslagcodes als in INCREASE_OVERTIME_MAP, dus het percentage komt
                 * daaruit; BONUS.GU staat er niet in en levert 100 op.
                 */
                const timeframePercentage = overtimePercentageOfBonusCode(bonusHourType);

                let partFrom = overtimeFrom;

                while (partFrom.getTime() < overtimeTo.getTime()) {
                    /** Trappercentage: 130% zolang de band van 3 uur nog niet vol is. */
                    const tierPercentage = settled130Minutes < OVERTIME_130_MAX_MINUTES ? 130 : 150;

                    // Het hoogste van tijdvak en trap; 200% blijft 200% (feestdag/maandagnacht).
                    const percentage = Math.max(timeframePercentage, tierPercentage);

                    /**
                     * We hebben maximnaal 3 uur aan 130% uren. Als we er meer hebben wordt het 150%.
                     * We kijken hier of we daar overheen gaan, zo ja dan wordt de rest doorgeschoven
                     */
                    const partMinutes =
                        percentage === 130
                            ? Math.min(
                                  Utils.minuteDuration(overtimeTo, partFrom),
                                  OVERTIME_130_MAX_MINUTES - settled130Minutes
                              )
                            : Utils.minuteDuration(overtimeTo, partFrom);

                    const partTo = Utils.addMinutes(new Date(partFrom), partMinutes);

                    settle({ settleHourType: `${percentage}`, settleFrom: partFrom, settleTo: partTo });

                    if (percentage === 130) {
                        settled130Minutes += partMinutes;
                    }

                    partFrom = partTo;
                }
            };

            // Main logic 😉
            if (isHoliday) {
                // Indien feestdag, dan alles overwerk 200%
                settle({ settleHourType: '200' }); // Overwerk 200% uitbetalen; Overwerk
            } else {
                if (isWeekend) {
                    if (isSunday) {
                        // Op zondag alles 200%; binnen de weekruimte als MU + 200V
                        settleWeekendHours({ weekendBonusHourType: BONUS['200V'], weekendHourType: '200Z' });
                    } else if (isSaturday) {
                        settleSaturday();
                    }
                } else if (isWeekday) {
                    if (isMonday) {
                        settleMonday();
                    } else {
                        if (isEarlyMorning) {
                            settleEarlyMornings();
                        } else {
                            // settleContractHours bepaalt zelf wat nog binnen het budget past
                            // en wat als overwerk geboekt moet worden
                            settleWeekdays();
                        }
                    }
                }
            }

            settledContractMinutes += settledInTimeframe;
        }
    }

    /**
     * Bij start werkdag vóór of om 00.00 uur en doorwerken na 05.00 uur tellen uren na 05.00
     * voor 150%.
     *
     * Middernacht wordt van {@link day} afgeleid en niet van {@link startDate}: anders zou de
     * vergelijking alleen waar zijn bij een start op exact 00:00:00, en valt een dienst die om
     * bijv. 23:00 de vorige dag begint er onterecht buiten.
     */
    private isStartedOn0000AndContinueAfter0500(startDate: Date, endDate: Date, day: WeekDay) {
        const midnight = Utils.setTime(new Date(day.date), 0, 0);

        const day0500 = Utils.setTime(new Date(day.date), 5, 0);

        return Utils.getTimeOrNull(startDate) <= midnight.getTime() && Utils.getTimeOrNull(endDate) > day0500.getTime();
    }

    private calculateRealisationTimeframes(realisation: Realisation, timeframes: Timeframe[]) {
        const beginDate = new Date(realisation.begindate);
        const endDate = new Date(realisation.enddate);

        let from = new Date(beginDate);

        for (const block of this.getTimeframeBonuses(beginDate, endDate)) {
            if (from.getTime() < block.from.getTime() || from.getTime() > block.to.getTime()) {
                continue;
            }

            const to = new Date(Math.min(endDate.getTime(), block.to.getTime()));

            if (to.getTime() === from.getTime()) {
                continue;
            }

            timeframes.push({
                from,
                to,
                minutes: Utils.minuteDuration(to, from),
                type: block.type,
                realisation,
            });

            if (endDate.getTime() <= block.to.getTime()) {
                break;
            }

            from = new Date(to);
        }
    }

    private getTimeframeBonuses(beginDate: Date, endDate: Date): TimeframeBonus[] {
        const bonuses: TimeframeBonus[] = [];
        const currentDate = Utils.setTime(new Date(beginDate), 0, 0);

        while (currentDate.getTime() < endDate.getTime()) {
            bonuses.push(...this.getDailyTimeframeBonuses(currentDate));
            currentDate.setDate(currentDate.getDate() + 1);
        }

        return bonuses;
    }

    private getDailyTimeframeBonuses(date: Date): TimeframeBonus[] {
        const dayOfWeek = date.getDay();

        if (dayOfWeek === SATURDAY_INDEX) {
            return [
                this.createTimeframeBonus(date, 0, 21, BONUS['150V']),
                this.createTimeframeBonus(date, 21, 24, BONUS['200V']),
            ];
        }

        if (dayOfWeek === SUNDAY_INDEX) {
            return [this.createTimeframeBonus(date, 0, 24, BONUS['200V'])];
        }

        const startOfDayBlocks =
            dayOfWeek === MONDAY_INDEX
                ? [
                      this.createTimeframeBonus(date, 0, 4, BONUS['200V']),
                      this.createTimeframeBonus(date, 4, 5, BONUS['150V']),
                  ]
                : [this.createTimeframeBonus(date, 0, 5, BONUS['150V'])];

        return [...startOfDayBlocks, ...this.getRegularTimeframeBonuses(date)];
    }

    private getRegularTimeframeBonuses(date: Date): TimeframeBonus[] {
        return [
            this.createTimeframeBonus(date, 5, 7, BONUS['130V']),
            this.createTimeframeBonus(date, 7, 16, BONUS.GU),
            this.createTimeframeBonus(date, 16, 18, BONUS['130V']),
            this.createTimeframeBonus(date, 18, 24, BONUS['150V']),
        ];
    }

    private createTimeframeBonus(
        date: Date,
        fromHour: number,
        toHour: number,
        type: BONUS,
        toDate = date
    ): TimeframeBonus {
        return {
            from: Utils.setTime(new Date(date), fromHour, 0),
            to: Utils.setTime(new Date(toDate), toHour, 0),
            type,
        };
    }
}
